import { Badge, cn } from "ui";
import type { BadgeProps } from "ui";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";
import { enumValueKey } from "../util/options";

/** Semantic tone groups mapped to soft-bg + colored-text token pairs. */
type StatusTone = "success" | "warning" | "info" | "neutral" | "error";

const TONE_CLASS: Record<StatusTone, string> = {
  success: "bg-ticketon-success-bg text-ticketon-success-text",
  warning: "bg-ticketon-warning-bg text-ticketon-warning-text",
  info: "bg-blue-50 text-ticketon-dark-blue",
  neutral: "bg-gray-100 text-gray-600",
  error: "bg-ticketon-error-bg text-ticketon-error-text",
};

/**
 * Maps the rewards status enum values to a consistent tone. Covers
 * ProgramStatus, RuleStatus, CatalogItemStatus, RedemptionStatus,
 * FulfillmentStatus and TransactionStatus values, which never collide.
 */
const STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: "success",
  CONFIRMED: "success",
  COMPLETED: "success",
  FULFILLED: "success",
  POSTED: "success",
  PENDING: "warning",
  IN_PROGRESS: "info",
  COMING_SOON: "info",
  DRAFT: "neutral",
  NOT_STARTED: "neutral",
  PAUSED: "warning",
  INACTIVE: "neutral",
  OUT_OF_STOCK: "warning",
  ARCHIVED: "neutral",
  CANCELLED: "error",
  REVERSED: "error",
  EXPIRED: "error",
  FAILED: "error",
};

export interface IStatusBadgeProps {
  status: string;
  t?: Translator;
  size?: BadgeProps["size"];
}

/** A status pill that colors itself based on the status value. */
export function StatusBadge({ status, t: tOverride, size = "default" }: IStatusBadgeProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  const tone = STATUS_TONE[status] ?? "neutral";
  return (
    <Badge
      variant="neutral"
      size={size}
      className={cn("border-transparent font-medium", TONE_CLASS[tone])}
      data-testid="status-badge"
    >
      {t(enumValueKey("rewards.status", status))}
    </Badge>
  );
}
